import { createContext, PropsWithChildren, useState } from 'react';
import { useSearchParams } from 'react-router';

const ActivityPaneContext = createContext<boolean>(false);

/**
 * Captures whether THIS miniapp instance was launched inside tenant-portal's activity-center pane.
 *
 * The host appends `activity-pane=true` to the FIRST framed URL only; the miniapp's internal
 * navigations build fresh paths (event -> schedule -> session) that drop the query, so the marker
 * cannot be re-read from the URL on every view. Persisting it is the wrong fix — `sessionStorage`
 * is shared across same-origin frames in the same tab, so a later standalone load would inherit a
 * stale flag. Instead the marker is read from the boot URL exactly once and held for the life of
 * this React tree: a value that lives and dies with this app instance and is never shared with
 * another load, so a fresh standalone launch reads its own URL and starts clean.
 *
 * Read at first render, synchronously, so the chrome decision (hidden back button) is right on the
 * first paint — no async host round-trip, no flash. The two internal hops that carry
 * `activity-pane=true` in the URL keep it truthful so a reload inside the pane re-captures it.
 *
 * Must sit inside the router so the boot URL's query is readable.
 */
export const ActivityPaneProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [searchParams] = useSearchParams();
  // Lazy initialiser: runs on the first render only, so later navigations that drop the query can't
  // clear it, and it is never recomputed from a URL that no longer carries the marker.
  const [isActivityPane] = useState(() => searchParams.get('activity-pane') === 'true');

  return <ActivityPaneContext.Provider value={isActivityPane}>{children}</ActivityPaneContext.Provider>;
};

export { ActivityPaneContext };
